import ControlPanelController from "./control_panel_controller"

export default class extends ControlPanelController {
  static targets = [
    "viewingBids", "bidEvent", "bidLeveling",
    "bidHistoryLog", "showGcalGuestList",
    "showContractorsEmailsBidDocsAddenda",
    "shouldAttachPdfBidContractorsEmail", "unitPricingAnyFile",
    "recipientsSelect", "recipientsList",
    "consultantCanBid", "consultantProjectsSection",
    "consultantProjectsSelect", "consultantProjectsList",
    "bidResultsListGetsInvite", "inviteModal", "inviteModalTitle", "inviteModalBody", "inviteModalButton"
  ]

  static values = {
    updateUrl: String
  }

  addRecipient() {
    const select = this.recipientsSelectTarget
    const email = select.value
    if (!email) return

    // Avoid duplicates
    const existing = this.recipientsListTarget.querySelectorAll("[data-email]")
    for (const tag of existing) {
      if (tag.dataset.email === email) {
        select.value = ""
        return
      }
    }

    const tag = document.createElement("span")
    tag.className = "inline-flex items-center gap-1 bg-gray-200 text-[13px] text-gray-800 px-2.5 py-1 rounded-full"
    tag.dataset.email = email
    tag.innerHTML = `${this._escapeHtml(email)}
      <button type="button" class="text-gray-400 hover:text-red-500 cursor-pointer"
              data-action="click->cpanel-bidding#removeRecipient">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>`

    this.recipientsListTarget.appendChild(tag)
    select.value = ""
  }

  removeRecipient(event) {
    const tag = event.currentTarget.closest("[data-email]")
    if (tag) tag.remove()
  }

  // ── Consultant may also bid: per-project picker (Phelipe 2026-09-08) ──

  toggleConsultantProjects() {
    if (!this.hasConsultantProjectsSectionTarget) return
    this.consultantProjectsSectionTarget.classList.toggle("hidden", !this.consultantCanBidTarget.checked)
  }

  addConsultantProject() {
    const select = this.consultantProjectsSelectTarget
    const id = select.value
    if (!id) return
    const name = select.options[select.selectedIndex]?.text || id

    if (this.consultantProjectsListTarget.querySelector(`[data-project-id="${id}"]`)) {
      select.value = ""
      return
    }

    const tag = document.createElement("span")
    tag.className = "inline-flex items-center gap-1 bg-gray-200 text-[13px] text-gray-800 px-2.5 py-1 rounded-full"
    tag.dataset.projectId = id
    tag.innerHTML = `${this._escapeHtml(name)}
      <button type="button" class="text-gray-400 hover:text-red-500 cursor-pointer"
              data-action="click->cpanel-bidding#removeConsultantProject">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>`

    this.consultantProjectsListTarget.appendChild(tag)
    select.value = ""
  }

  removeConsultantProject(event) {
    const tag = event.currentTarget.closest("[data-project-id]")
    if (tag) tag.remove()
  }

  _collectConsultantProjects() {
    if (!this.hasConsultantProjectsListTarget) return []
    return Array.from(this.consultantProjectsListTarget.querySelectorAll("[data-project-id]"))
      .map(tag => tag.dataset.projectId)
  }

  _collectRecipients() {
    const tags = this.hasRecipientsListTarget
      ? this.recipientsListTarget.querySelectorAll("[data-email]")
      : []
    return Array.from(tags).map(tag => tag.dataset.email)
  }

  _escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  // CJC4-3 (2026-10-10): "Also send them the bid opening meeting invite".
  // The box goes back to how it was until the pop-up is confirmed; confirming
  // saves the page.
  askInviteChange(event) {
    const box = event.target
    this._pendingInvite = box.checked
    box.checked = !box.checked
    const modal = this.inviteModalTarget
    const side = this._pendingInvite ? "on" : "off"
    this.inviteModalTitleTarget.textContent = modal.dataset[`${side}Title`]
    this.inviteModalBodyTarget.textContent = modal.dataset[`${side}Body`]
    this.inviteModalButtonTarget.textContent = modal.dataset[`${side}Button`]
    modal.hidden = false
  }

  confirmInviteChange() {
    this.bidResultsListGetsInviteTarget.checked = this._pendingInvite
    this.inviteModalTarget.hidden = true
    this.save()
  }

  cancelInviteChange() {
    if (this.hasInviteModalTarget) this.inviteModalTarget.hidden = true
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async save() {
    const body = {
      company: {
        viewing_bids: this.hasViewingBidsTarget ? this.viewingBidsTarget.value : undefined,
        bid_event: this.hasBidEventTarget ? this.bidEventTarget.value : undefined,
        bid_leveling: this.hasBidLevelingTarget ? this.bidLevelingTarget.value : undefined,
        bid_history_log: this.hasBidHistoryLogTarget ? this.bidHistoryLogTarget.checked : undefined,
        show_gcal_guest_list: this.hasShowGcalGuestListTarget ? this.showGcalGuestListTarget.checked : undefined,
        show_contractors_emails_bid_docs_addenda: this.hasShowContractorsEmailsBidDocsAddendaTarget ? this.showContractorsEmailsBidDocsAddendaTarget.checked : undefined,
        should_attach_pdf_bid_contractors_email: this.hasShouldAttachPdfBidContractorsEmailTarget ? this.shouldAttachPdfBidContractorsEmailTarget.checked : undefined,
        unit_pricing_any_file: this.hasUnitPricingAnyFileTarget ? this.unitPricingAnyFileTarget.checked : undefined,
        bid_opening_email_recipients: this._collectRecipients(),
        consultant_can_bid: this.hasConsultantCanBidTarget ? this.consultantCanBidTarget.checked : undefined,
        bid_results_list_gets_invite: this.hasBidResultsListGetsInviteTarget ? this.bidResultsListGetsInviteTarget.checked : undefined,
        consultant_bidder_project_ids: this._collectConsultantProjects()
      }
    }

    try {
      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._showToast(data.message || "Settings saved")
      } else {
        this._showToast(data.errors?.join(", ") || "Error saving settings", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }
}
